import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  Mail, Settings, RefreshCw, Check, X, AlertCircle, Clock, FileText,
  Trash2, CheckCircle, XCircle, Eye, ExternalLink, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { GmailHistoryImport } from './GmailHistoryImport';
import { GmailCleanup } from './GmailCleanup';
import { GmailExtraAccounts } from './GmailExtraAccounts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GmailConfigPanel = ({ onPendingCountChange }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [checking, setChecking] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingExpenses, setPendingExpenses] = useState([]);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formInterval, setFormInterval] = useState(30);
  const [formEnabled, setFormEnabled] = useState(true);

  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/config`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        if (data.configured) {
          setFormEmail(data.email);
          setFormInterval(data.check_interval_minutes);
          setFormEnabled(data.enabled);
        }
      }
    } catch (e) {
      console.error('Error fetching gmail config:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/pending`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPendingExpenses(data.pending || []);
        onPendingCountChange?.(data.total || 0);
      }
    } catch (e) {
      console.error('Error fetching pending:', e);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchPending();
  }, [fetchConfig, fetchPending]);

  const handleSave = async () => {
    if (!formEmail || !formPassword) {
      toast.error('Email y contraseña son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/config`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formEmail,
          app_password: formPassword,
          check_interval_minutes: formInterval,
          enabled: formEnabled
        })
      });
      if (res.ok) {
        toast.success('Gmail configurado correctamente');
        setShowSetupDialog(false);
        setFormPassword('');
        fetchConfig();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (e) {
      toast.error('Error de conexion');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/test`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Error en la prueba');
      }
    } catch (e) {
      toast.error('Error de conexion');
    } finally {
      setTesting(false);
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/check-now`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchPending();
        fetchConfig();
      } else {
        toast.error(data.detail || 'Error al revisar correos');
      }
    } catch (e) {
      toast.error('Error de conexion');
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar la configuracion de Gmail?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/config`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Configuracion eliminada');
        setConfig(null);
        fetchConfig();
      }
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const handleApproveAll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/approve-all`, { method: 'PUT', headers });
      const j = await res.json();
      if (!res.ok) throw new Error();
      toast.success(`${j.approved} factura${j.approved === 1 ? '' : 's'} aprobada${j.approved === 1 ? '' : 's'}`);
      fetchPending();
    } catch (e) {
      toast.error('Error al aprobar');
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/approve/${id}`, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        toast.success('Gasto aprobado');
        fetchPending();
      }
    } catch (e) {
      toast.error('Error al aprobar');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('¿Rechazar y eliminar este gasto?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/gmail/reject/${id}`, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        toast.success('Gasto rechazado');
        fetchPending();
      }
    } catch (e) {
      toast.error('Error al rechazar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('es-ES', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  if (loading) return null;

  return (
    <>
      {/* Gmail Status Card */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-red-500" />
            <h3 className="font-semibold text-gray-900">Gmail - Facturas automaticas</h3>
          </div>
          <div className="flex items-center gap-2">
            {pendingExpenses.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPendingDialog(true)}
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                data-testid="gmail-pending-btn"
              >
                <AlertCircle size={14} className="mr-1" />
                {pendingExpenses.length} pendiente{pendingExpenses.length !== 1 ? 's' : ''}
              </Button>
            )}
            {config?.configured && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCheckNow}
                  disabled={checking}
                  data-testid="gmail-check-now-btn"
                >
                  <RefreshCw size={14} className={checking ? 'animate-spin mr-1' : 'mr-1'} />
                  {checking ? 'Revisando...' : 'Revisar ahora'}
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSetupDialog(true)}
              data-testid="gmail-settings-btn"
            >
              <Settings size={16} />
            </Button>
          </div>
        </div>

        <div className="p-4">
          {!config?.configured ? (
            <div className="text-center py-4">
              <Mail size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500 mb-3">
                Conecta una cuenta de Gmail para recibir facturas automaticamente
              </p>
              <Button
                size="sm"
                onClick={() => setShowSetupDialog(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="gmail-setup-btn"
              >
                <Mail size={14} className="mr-1" />
                Configurar Gmail
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${config.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{config.email}</p>
                  <p className="text-xs text-gray-500">
                    {config.last_check
                      ? `Ultima revision: ${formatDate(config.last_check)} — ${config.last_check_result || ''}`
                      : 'Sin revisiones aun'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {config.total_processed || 0} procesadas
                </Badge>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} className="text-red-500" />
              Configurar Gmail
            </DialogTitle>
            <DialogDescription>
              Conecta una cuenta de Gmail dedicada para que las facturas de proveedores se procesen automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Instructions */}
            <div className="p-3 bg-blue-50 rounded-lg text-sm space-y-1">
              <p className="font-medium text-blue-800 flex items-center gap-1">
                <Shield size={14} />
                Como configurar:
              </p>
              <ol className="text-blue-700 list-decimal pl-4 space-y-0.5">
                <li>Activa la verificacion en 2 pasos en la cuenta Gmail</li>
                <li>Ve a <span className="font-mono text-xs">myaccount.google.com/apppasswords</span></li>
                <li>Crea una contrasena de aplicacion</li>
                <li>Pegala aqui abajo</li>
              </ol>
            </div>

            <div>
              <Label className="text-sm text-gray-600">Correo Gmail</Label>
              <Input
                type="email"
                placeholder="facturas@tudominio.com"
                value={formEmail}
                onChange={e => setFormEmail(e.target.value)}
                data-testid="gmail-email-input"
              />
            </div>

            <div>
              <Label className="text-sm text-gray-600">Contrasena de aplicacion</Label>
              <Input
                type="password"
                placeholder={config?.configured ? '••••••••••••' : 'xxxx xxxx xxxx xxxx'}
                value={formPassword}
                onChange={e => setFormPassword(e.target.value)}
                data-testid="gmail-password-input"
              />
              {config?.configured && (
                <p className="text-xs text-gray-400 mt-1">Deja vacio para mantener la actual</p>
              )}
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-sm text-gray-600">Intervalo de revision (min)</Label>
                <Input
                  type="number"
                  min="5"
                  max="1440"
                  value={formInterval}
                  onChange={e => setFormInterval(parseInt(e.target.value) || 30)}
                  data-testid="gmail-interval-input"
                />
              </div>
            </div>

            {config?.configured && (
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={testing}
                  className="flex-1"
                  data-testid="gmail-test-btn"
                >
                  {testing ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                  Probar conexion
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-500 hover:text-red-700"
                  data-testid="gmail-delete-btn"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="gmail-save-btn"
            >
              {saving ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}
              {saving ? 'Conectando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {config?.configured && <GmailExtraAccounts />}
      <GmailHistoryImport onFinished={fetchPending} />
      <GmailCleanup onFinished={fetchPending} />

      {/* Pending Expenses Dialog */}
      <Dialog open={showPendingDialog} onOpenChange={setShowPendingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={20} className="text-amber-500" />
              Facturas pendientes de revision
            </DialogTitle>
            <DialogDescription>
              Estas facturas fueron detectadas automaticamente desde el correo. Revisalas y aprueba o rechaza cada una.
            </DialogDescription>
          </DialogHeader>

          {pendingExpenses.some(e => !e.possible_duplicate) && (
            <Button onClick={handleApproveAll} className="w-full bg-green-600 hover:bg-green-700" data-testid="gmail-approve-all-btn">
              Aprobar todas las que no son duplicadas ({pendingExpenses.filter(e => !e.possible_duplicate).length})
            </Button>
          )}
          <ScrollArea className="max-h-96">
            <div className="space-y-3 py-2">
              {pendingExpenses.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <CheckCircle size={32} className="mx-auto mb-2" />
                  <p>No hay facturas pendientes</p>
                </div>
              ) : (
                pendingExpenses.map(expense => (
                  <div key={expense.id} className="p-3 border rounded-lg bg-amber-50 border-amber-200 space-y-2" data-testid={`pending-expense-${expense.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {expense.vendor || 'Proveedor desconocido'}
                        </p>
                        <p className="text-sm text-gray-600">{expense.description || '-'}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span>{expense.date}</span>
                          <span>|</span>
                          <span className="truncate">{expense.gmail_sender}</span>
                        </div>
                        {expense.gmail_subject && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            Asunto: {expense.gmail_subject}
                          </p>
                        )}
                      </div>
                      <p className="text-lg font-bold text-red-600 ml-3">
                        {(expense.amount || 0).toFixed(2)}€
                      </p>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-amber-200">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {expense.category}
                        </Badge>
                        {expense.possible_duplicate && (
                          <Badge className="text-xs bg-red-100 text-red-700 hover:bg-red-100" data-testid={`pending-duplicate-${expense.id}`}>
                            Posible duplicado
                          </Badge>
                        )}
                        {expense.invoice_url && (
                          <a
                            href={`${API_URL}${expense.invoice_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <Eye size={14} />
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(expense.id)}
                          className="text-red-500 hover:bg-red-50 h-8"
                          data-testid={`reject-${expense.id}`}
                        >
                          <XCircle size={14} className="mr-1" />
                          Rechazar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(expense.id)}
                          className="bg-green-600 hover:bg-green-700 h-8"
                          data-testid={`approve-${expense.id}`}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Aprobar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GmailConfigPanel;
